import type {
  Account,
  AccountRelatedData,
  CreateAccountPayload,
  UpdateAccountPayload,
} from "@/features/accounts/types";
import { computeAccountCreditBalance } from "@/features/accounts/lib/account-credit-balances";
import { getBackendClient, type TableName } from "@/lib/backend";
import { ACCOUNT_SELECT } from "@/lib/db/selects";
import { createAccountSchema, updateAccountSchema } from "@/lib/schemas/accounts";
import { cast } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const INVOICE_SELECT = `
  id, uid, date, notes, total_amount, total_paid, balance, payment_status,
  account_id, created_at, pdf_url,
  gl_accounts!gl_invoices_account_id_fkey ( id, name, uid )
`;

const PURCHASE_ORDER_SELECT = `
  id, uid, date, notes, total_amount, total_paid, balance, payment_status,
  is_paid, product_count, account_id, created_at,
  gl_accounts!gl_purchase_orders_account_id_fkey ( id, name, uid )
`;

const ESTIMATE_SELECT = `
  id, uid, date, notes, total_amount, total_paid, balance, payment_status,
  is_converted, is_sample, is_quick, detail_completed_at, project_id,
  converted_date, converted_invoice_id, account_id, created_at,
  gl_accounts!gl_estimates_account_id_fkey ( id, name, uid )
`;

const CUSTOMER_PAYMENT_SELECT = `
  id, uid, amount, date, method, reference, invoice_id, account_id, created_at,
  gl_accounts!gl_customer_payments_account_id_fkey ( id, name, uid ),
  gl_invoices!gl_customer_payments_invoice_id_fkey ( id, uid )
`;

const VENDOR_PAYMENT_SELECT = `
  id, uid, amount, date, method, reference, purchase_order_id, account_id, created_at,
  gl_accounts!gl_vendor_payments_account_id_fkey ( id, name, uid ),
  gl_purchase_orders!gl_vendor_payments_purchase_order_id_fkey ( id, uid )
`;

const CREDIT_SELECT = `
  id, uid, amount, date, method, reference, account_id, estimate_id, created_at
`;

const CREDIT_APPLICATION_SELECT = `
  id, credit_id, estimate_id, invoice_id, amount_applied, is_reversal, applied_at, reverses_application_id,
  gl_invoices!gl_credit_applications_invoice_id_fkey ( id, uid ),
  gl_estimates!gl_credit_applications_estimate_id_fkey ( id, uid ),
  gl_customer_credits!gl_credit_applications_credit_id_fkey ( id, uid )
`;

/** Rows of a list query, with a missing payload normalized to an empty list. */
function rowsOf<Row>(result: { data: Row[] | null }): Row[] {
  return result.data ?? [];
}

/** Fails the whole load when any related-table query failed, naming each table. */
function assertRelatedQueriesSucceeded(
  results: { label: string; error: { message: string } | null }[],
): void {
  const messages = results.flatMap(({ label, error }) =>
    error ? [`${label}: ${error.message}`] : [],
  );

  if (messages.length > 0) {
    throw new Error(`Failed to load account related data: ${messages.join("; ")}`);
  }
}

/** One batched read of the applications behind the given credits. */
async function fetchCreditApplications(creditIds: string[]): Promise<unknown[]> {
  if (creditIds.length === 0) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_credit_applications")
    .select(CREDIT_APPLICATION_SELECT)
    .in("credit_id", creditIds)
    .order("applied_at", { ascending: false })
    .limit(500);

  if (error) {
    throw new Error(`credit_applications: ${error.message}`);
  }
  return data ?? [];
}

/** The account's slice of one related table, newest first. */
function latestByAccount(table: TableName, select: string, accountId: string, limit = 100) {
  return backend.data
    .from(table)
    .select(select)
    .eq("account_id", accountId)
    .order("date", { ascending: false })
    .limit(limit);
}

/**
 * The account's invoices, newest first, excluding voided shells. The account
 * timeline and totals derive from this same set, so an undone conversion's
 * neutralized invoice must not inflate "billed" or appear as an active row.
 */
function latestInvoicesByAccount(select: string, accountId: string) {
  return latestByAccount("gl_invoices", select, accountId).is("voided_at", null);
}

export async function getAccountRelated(id: string) {
  const [
    accountRes,
    invoicesRes,
    purchaseOrdersRes,
    estimatesRes,
    customerPaymentsRes,
    vendorPaymentsRes,
    creditsRes,
    productsRes,
  ] = await Promise.all([
    backend.data.from("gl_accounts").select(ACCOUNT_SELECT).eq("id", id).single(),
    latestInvoicesByAccount(INVOICE_SELECT, id),
    latestByAccount("gl_purchase_orders", PURCHASE_ORDER_SELECT, id),
    latestByAccount("gl_estimates", ESTIMATE_SELECT, id),
    latestByAccount("gl_customer_payments", CUSTOMER_PAYMENT_SELECT, id),
    latestByAccount("gl_vendor_payments", VENDOR_PAYMENT_SELECT, id),
    latestByAccount("gl_customer_credits", CREDIT_SELECT, id, 200),
    // Bare ids only: products never render on the detail route, but their
    // foreign key rejects an account delete, so the delete guard needs them.
    backend.data.from("gl_products").select("id").eq("account_id", id).limit(100),
  ]);

  if (accountRes.error) {
    throw new Error(accountRes.error.message);
  }

  assertRelatedQueriesSucceeded([
    { label: "invoices", error: invoicesRes.error },
    { label: "purchase_orders", error: purchaseOrdersRes.error },
    { label: "estimates", error: estimatesRes.error },
    { label: "customer_payments", error: customerPaymentsRes.error },
    { label: "vendor_payments", error: vendorPaymentsRes.error },
    { label: "credits", error: creditsRes.error },
    { label: "products", error: productsRes.error },
  ]);

  const credits = cast<AccountRelatedData["credits"]>(rowsOf(creditsRes));
  const creditIds = credits
    .map((credit) => credit.id)
    .filter((creditId): creditId is string => Boolean(creditId));

  const creditApplications = cast<AccountRelatedData["credit_applications"]>(
    await fetchCreditApplications(creditIds),
  );

  return {
    account: cast<AccountRelatedData["account"]>(accountRes.data),
    invoices: cast<AccountRelatedData["invoices"]>(rowsOf(invoicesRes)),
    purchase_orders: cast<AccountRelatedData["purchase_orders"]>(rowsOf(purchaseOrdersRes)),
    estimates: cast<AccountRelatedData["estimates"]>(rowsOf(estimatesRes)),
    customer_payments: cast<AccountRelatedData["customer_payments"]>(rowsOf(customerPaymentsRes)),
    vendor_payments: cast<AccountRelatedData["vendor_payments"]>(rowsOf(vendorPaymentsRes)),
    credits,
    credit_applications: creditApplications,
    products: cast<AccountRelatedData["products"]>(rowsOf(productsRes)),
    account_credit_balance: computeAccountCreditBalance(credits, creditApplications),
  };
}

/** SQLSTATE `xan_update_account` raises when the row changed after it was read. */
const ACCOUNT_TARGET_CHANGED = "PT409";
const ACCOUNT_CHANGED_MESSAGE =
  "This account changed after you opened it. Reopen it and try again.";

/**
 * Creates an account through the idempotent seam: retrying the same
 * `commandId` with the same payload returns the first result, never a second row.
 */
export async function createAccount(commandId: string, payload: CreateAccountPayload) {
  const { data, error } = await backend.data.rpc<Account>("xan_create_account", {
    p_command_id: commandId,
    p_payload: createAccountSchema.parse(payload),
  });

  if (error) {
    throw new Error(error.message);
  }

  return cast<Account>(data);
}

/**
 * Updates the account the edit dialog opened. Balance recalculations also stamp
 * `updated_at`, so the version comes from a fresh read, and only a change to a
 * field the dialog showed counts as a conflict.
 */
export async function updateAccount(opened: Account, payload: UpdateAccountPayload) {
  const { data: current, error: readError } = await backend.data
    .from("gl_accounts")
    .select("name, uid, type, updated_at")
    .eq("id", opened.id)
    .maybeSingle();

  if (readError) {
    throw new Error(readError.message);
  }
  if (!current) {
    throw new Error("Account not found.");
  }
  if (current.name !== opened.name || current.uid !== opened.uid || current.type !== opened.type) {
    throw new Error(ACCOUNT_CHANGED_MESSAGE);
  }

  const { data, error } = await backend.data.rpc<Account>("xan_update_account", {
    p_account_id: opened.id,
    p_expected_updated_at: current.updated_at,
    p_patch: updateAccountSchema.parse(payload),
  });

  if (error?.code === ACCOUNT_TARGET_CHANGED) {
    throw new Error(ACCOUNT_CHANGED_MESSAGE);
  }
  if (error) {
    throw new Error(error.message);
  }

  return cast<Account>(data);
}

/**
 * Deletes an account by ID.
 *
 * ARCHITECTURAL DEBT: This should be a backend-owned atomic deletion RPC that
 * validates all protected relationships (customer payments, vendor payments,
 * credits, products) before deleting. The current implementation is a direct
 * DELETE query that relies on database foreign key constraints for enforcement.
 * The detail page provides a preflight guard via describeAccountDeleteBlock(),
 * but the list page has no access to related data and cannot show blocking
 * relationships before attempting the delete. A backend RPC would provide
 * consistent validation and clearer error messages across all call sites.
 *
 * Protected relationships (reject deletion):
 * - customer_payments, vendor_payments, credits, products
 *
 * Detachable relationships (account_id nulled on delete):
 * - invoices, estimates, purchase_orders
 */
export async function deleteAccount(id: string) {
  const { data, error } = await backend.data.from("gl_accounts").delete().eq("id", id).select("id");

  if (error) {
    throw new Error(error.message);
  }
  if (!data || data.length === 0) {
    throw new Error("Account not found.");
  }

  return { success: true };
}
